import React, { useState, useRef, useEffect, type ReactElement, type KeyboardEvent, useMemo } from "react";
import { NavLink, useNavigate } from "react-router";
import cn from "classnames";
import { type SegmentProps } from "../Segment/Segment";

import styles from "./MoreMenu.module.scss";
import moboConfigurator from "moboConfigurator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/pro-solid-svg-icons";

type MoreMenuProps = {
  segments: ReactElement<SegmentProps>[];
  basePath: string;
};

const MoreMenu: React.FC<MoreMenuProps> = ({ segments, basePath }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuItemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const accountId = useMemo(() => moboConfigurator.getMoboId(), []);

  // Initialize refs for menu items
  useEffect(() => {
    menuItemRefs.current = Array(segments.length).fill(null);
  }, [segments]);

  // Handle keyboard navigation and outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".more-menu-container")) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "Escape":
          setIsOpen(false);
          moreButtonRef.current?.focus();
          break;
        case "ArrowDown":
          event.preventDefault();
          focusNextMenuItem();
          break;
        case "ArrowUp":
          event.preventDefault();
          focusPreviousMenuItem();
          break;
        case " ":
        case "Enter":
          event.preventDefault();
          if (document.activeElement?.getAttribute("role") === "menuitem") {
            const activeElement = document.activeElement as HTMLAnchorElement;
            const to = activeElement.getAttribute("data-to");
            if (to) {
              setIsOpen(false);
              navigate(to);
            }
          }
          break;
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown as unknown as EventListener);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown as unknown as EventListener);
    };
  }, [isOpen, navigate]);

  // Focus management - focus first item when menu opens
  useEffect(() => {
    if (isOpen && menuItemRefs.current.length > 0) {
      setTimeout(() => {
        menuItemRefs.current[0]?.focus();
      }, 10);
    }
  }, [isOpen]);

  // Check if any segment in the dropdown is active
  const hasActiveSegment = segments.some((segment) => {
    const segmentPath = segment.props.to ? `${basePath}/${segment.props.to}` : basePath;
    const currentPath = window.location.pathname;
    return currentPath === segmentPath || currentPath.startsWith(`${segmentPath}/`);
  });

  // Focus navigation helpers
  const focusNextMenuItem = () => {
    const currentIndex = menuItemRefs.current.findIndex((item) => item === document.activeElement);
    const nextIndex = currentIndex < menuItemRefs.current.length - 1 ? currentIndex + 1 : 0;
    menuItemRefs.current[nextIndex]?.focus();
  };

  const focusPreviousMenuItem = () => {
    const currentIndex = menuItemRefs.current.findIndex((item) => item === document.activeElement);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItemRefs.current.length - 1;
    menuItemRefs.current[prevIndex]?.focus();
  };

  // Handle keyboard events on the More button
  const handleMoreButtonKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <div className={cn(styles.moreMenuContainer, { [styles["active-segment"]]: hasActiveSegment })}>
      <button
        ref={moreButtonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        onKeyDown={handleMoreButtonKeyDown}
        className={cn("segment", styles.moreButton)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls="more-segments-menu"
      >
        More
        <span className={styles.moreIcon} aria-hidden="true">
          {isOpen ? (
            <FontAwesomeIcon icon={faAngleUp} color={"gray"} />
          ) : (
            <FontAwesomeIcon icon={faAngleDown} color={"gray"} />
          )}
        </span>
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          id="more-segments-menu"
          className={styles.moreDropdown}
          role="menu"
          aria-orientation="vertical"
          tabIndex={-1}
        >
          {segments.map((segment, index) => {
            const to = segment.props.to ? `${basePath}/${segment.props.to}` : basePath;
            return (
              <NavLink
                ref={(el) => {
                  menuItemRefs.current[index] = el;
                }}
                key={segment.props.to || "/"}
                to={{ pathname: to, hash: accountId ? `#moboId=${accountId}` : undefined }}
                data-to={to}
                className={({ isActive }) => cn(styles.dropdownItem, { [styles.active]: isActive })}
                onClick={() => {
                  setIsOpen(false);
                }}
                role="menuitem"
                tabIndex={0}
              >
                {segment.props.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MoreMenu;
